<script setup lang="ts">
import { isRunningInTWA } from '~/utils/browserInfo';

type Props = {
  msToShow?: number;
};

const { msToShow = 5000 } = defineProps<Props>();

const { t } = useTexts();
const { linkToAppDownload } = useLinks();
const { isDesktop, isLayoutMobileOrTablet } = useLayout();
const { openToaster } = useLayoutStore();
const isAppEnvironment = isRunningInTWA();

const { isInstallAppPopupEnabled, isWithinMigrationPeriod } =
  useFeatureFlagsNew();

const { isStandAlone, isIosSafari } = useLayout();
const { bottomMenu, menuOpen } = storeToRefs(useLayoutStore());
const { currentRouteName } = useRoutes();

// Detecção de bots para bypass automático
const { isBot } = usePerformanceBotBypass();

const bottomMenuHeight = computed(() => {
  const bottomMenuHeight = bottomMenu.value.height;

  return bottomMenuHeight ? `${bottomMenuHeight + 10}px` : '10px';
});

const cookieName = 'install_app_popup_closed';

const wasClosedPreviously = useCookie(cookieName, {
  default: () => false,
  path: '/',
  expires: new Date(new Date().setDate(new Date().getDate() + 7)),
  // expires: new Date(),
});

const isCountdownElapsed = ref(false);

const isVisible = computed(() => {
  return linkToAppDownload.value && !wasClosedPreviously.value;
});

const showInstallAppPopup = computed(() => {
  // Nunca mostrar popup para bots (Googlebot, Lighthouse, GTmetrix)
  if (isBot.value) {
    return false;
  }

  if (currentRouteName.value && currentRouteName.value?.includes('sports')) {
    return false;
  }

  return (
    isVisible.value &&
    !isStandAlone.value &&
    isInstallAppPopupEnabled.value &&
    (isLayoutMobileOrTablet.value ? !menuOpen.value : true) &&
    !isWithinMigrationPeriod.value &&
    !isAppEnvironment
  );
});

function setCookieClosed(e?: Event) {
  wasClosedPreviously.value = true;
  if (e) {
    e.preventDefault();
    e.stopPropagation();
  }
}

const showIosTutorial = ref(false);

const closeIosTutorial = () => {
  showIosTutorial.value = false;
  setCookieClosed();
};

function installAppPwa() {
  if (deferredPrompt) {
    deferredPrompt.prompt();

    deferredPrompt.userChoice.then((choiceResult: any) => {
      if (choiceResult.outcome === 'accepted') {
        openToaster({
          message: t('installAppPopup.success'),
          type: 'success',
          time: 10000,
        });
      } else {
        openToaster({
          message: t('installAppPopup.dismissed'),
          type: 'error',
        });
      }
      deferredPrompt = null;
    });
  } else if (!isStandAlone.value && isIosSafari.value) {
    showIosTutorial.value = true;
    setCookieClosed();

    return;
  } else {
    openToaster({
      message: t('installAppPopup.error'),
      type: 'warning',
      time: 10000,
    });

    navigateTo(linkToAppDownload.value, {
      external: true,
      open: {
        target: '_blank',
      },
    });
  }

  setCookieClosed();
}

let deferredPrompt: any;

function handleBeforeInstallPrompt(e: Event) {
  e.preventDefault();
  deferredPrompt = e;
}

function removeBeforeInstallPromptListener() {
  window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
}

onMounted(() => {
  window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

  if (!showInstallAppPopup.value) return;

  setTimeout(() => {
    isCountdownElapsed.value = true;
  }, msToShow);
});

watch(wasClosedPreviously, (newValue) => {
  if (newValue) {
    removeBeforeInstallPromptListener();
  }
});
</script>

<template>
  <button
    v-if="showInstallAppPopup"
    id="divInstallAppPopup"
    :class="[
      $style.installAppPopup,
      { [$style.isDesktop]: isDesktop },
      { [$style.isCountdownElapsed]: isCountdownElapsed },
      `transition`,
    ]"
    @click="installAppPwa"
  >
    <div
      :class="$style.close"
      @click.prevent="setCookieClosed"
    >
      <NuxtIcon name="close" />
    </div>
    <div :class="$style.logoContainer">
      <BaseCommonLogo
        always-icon
        class="max-h-6 min-w-6 max-w-24 mr-0 !transform-none"
        custom-size
      />
    </div>
    <div :class="$style.textContainer">
      <h4>{{ $t('installAppPopup.title', { appName: appName() }) }}</h4>
      <p>
        {{ $t('installAppPopup.description') }}
        <img
          v-if="!isBot"
          :class="$style.iconGift"
          :src="getAssetsUrlForCDN('/assets/images/gift.png')"
        />
      </p>
    </div>
    <NuxtIcon
      :class="$style.iconArrow"
      name="arrows/angle_right"
    />
  </button>

  <div
    :class="[
      $style.iosTutorial,
      { [$style.iosTutorialOpen]: showIosTutorial },
      `transition`,
    ]"
    @click.prevent="closeIosTutorial"
  >
    <div
      :class="$style.closeIos"
      @click.prevent="closeIosTutorial"
    >
      <NuxtIcon name="close" />
    </div>
    <h4 :class="$style.iosTutorialTitle">
      <span v-html="$t('installAppPopup.iosTutorialTitle_1')"></span>
      <NuxtIcon
        :class="$style.iosShareIcon"
        name="ios_share"
      />
      {{ $t('installAppPopup.iosTutorialTitle_2') }}
      <u>
        {{ $t('installAppPopup.iosTutorialTitle_3') }}
        <NuxtIcon
          :class="$style.iosPlusIcon"
          name="ios_plus"
        />
      </u>
    </h4>
  </div>
</template>

<style module lang="postcss">
.installAppPopup {
  @apply z-[600] fixed inset-x-[10px] flex flex-row items-center gap-3 bg-bg-secondary rounded-lg py-2 px-3 shadow-lg shadow-black/20 overflow-hidden border-texts/10 border-solid border text-left cursor-pointer translate-y-20 opacity-0 transition-all duration-500;

  bottom: v-bind(bottomMenuHeight);

  &.isDesktop {
    @apply bottom-[12px] inset-x-[12px] left-auto min-w-[300px];
  }

  &.isCountdownElapsed {
    @apply translate-y-0 opacity-100;
  }

  &:before {
    @apply content-[''] absolute top-0 left-0 -z-10 w-full h-full bg-black/10;
  }

  &:hover {
    @apply opacity-100 brightness-125;

    &:before {
      @apply bg-black/20;
    }
  }

  .logoContainer {
    @apply flex items-center justify-center h-6 max-h-6 max-w-24 mr-0 overflow-hidden object-cover;
  }

  .textContainer {
    @apply leading-none flex flex-col;

    h4 {
      @apply font-bold text-xs text-texts;
    }

    p {
      @apply flex flex-row items-center gap-1 text-2xs text-texts;

      img {
        @apply w-[16px] h-[16px];
      }
    }
  }

  .iconGift {
    @apply w-[14px] h-[14px];
    animation: shake 3000ms linear infinite;
  }

  .close {
    @apply absolute top-1 right-1 flex items-center justify-center text-texts/50 text-2xs;

    &:hover {
      @apply text-texts scale-125;
    }
  }

  .iconArrow {
    @apply text-2xl text-texts ml-auto mr-1 my-auto;
    animation: arrowMove 800ms infinite;
  }
}

.iosTutorial {
  @apply fixed bottom-[15px] inset-x-[12px] mx-auto bg-white/95 z-[800] rounded-lg py-3 px-4 shadow-lg shadow-black/20 text-left cursor-pointer translate-y-20 opacity-0 transition-all duration-500 border border-solid;

  &.iosTutorialOpen {
    @apply translate-y-0 opacity-100;
  }

  &:after {
    @apply content-[''] absolute top-full left-1/2 transform -translate-x-1/2 -z-10 bg-white w-5 h-5 rotate-45 -mt-2.5;
  }

  .iosTutorialTitle {
    @apply font-semibold text-xs xs:text-sm text-black pr-2;

    strong {
      @apply font-bold text-[#2997ff];
    }

    u {
      @apply no-underline border-b border-solid border-black/40;
    }

    .iosShareIcon {
      @apply text-base inline-block align-top text-[#2997ff] ml-1;
    }

    .iosPlusIcon {
      @apply text-xs inline-block align-middle -mt-px text-[#555555];
    }
  }

  .closeIos {
    @apply absolute top-2 right-2 flex items-center justify-center text-black/50 text-xs;

    &:hover {
      @apply text-black scale-125;
    }
  }
}

@keyframes shake {
  0% {
    transform: scale(1);
  }
  60% {
    transform: scale(0.9);
  }
  65% {
    transform: scale(1.1) skew(0.004turn);
  }
  70% {
    transform: scale(1) skew(0.008turn);
  }
  75% {
    transform: scale(1) skew(0);
  }
  77% {
    transform: rotateZ(-15deg);
  }
  79% {
    transform: rotateZ(10deg);
  }
  81% {
    transform: rotateZ(-10deg);
  }
  83% {
    transform: rotateZ(6deg);
  }
  85% {
    transform: rotateZ(-4deg);
  }
}

@keyframes arrowMove {
  0%,
  100% {
    transform: translateX(2px);
  }
  50% {
    transform: translateX(-2px);
  }
}
</style>
